class TimerCounter extends HTMLElement {
  constructor() {
    super();

    this._intervalId = null;
    this._originalSeconds = 0;
    this._remainingSeconds = 0;
    this._autoplay = false;

    this._container = document.createElement('span');
    this._container.setAttribute('part', 'display');
    this.appendChild(this._container);
  }

  static get observedAttributes() {
    return ['minutes', 'seconds', 'autoplay'];
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal) return;
    switch (name) {
      case 'minutes':
      case 'seconds':
        this._syncDurationFromAttributes();
        break;
      case 'autoplay':
        this._autoplay = this.hasAttribute('autoplay');
        if (this._autoplay) this.start();
        break;
    }
  }

  connectedCallback() {
    this._syncDurationFromAttributes();
    if (this.hasAttribute('autoplay')) {
      this._autoplay = true;
      this.start();
    }
    this.render();
  }

  disconnectedCallback() {
    this.stop();
  }

  _syncDurationFromAttributes() {
    const minutes = parseInt(this.getAttribute('minutes') ?? '0', 10) || 0;
    const seconds = parseInt(this.getAttribute('seconds') ?? '0', 10) || 0;

    let total = minutes * 60 + seconds;
    if (total < 0) total = 0;
    if (total > 60 * 60) total = 60 * 60;

    this._originalSeconds = total;
    if (!this._intervalId) this._remainingSeconds = total;

    this.render();
  }

  start() {
    if (this._intervalId) return;

    if (this._originalSeconds <= 0) {
      this._remainingSeconds = 0;
      this.render();
      return;
    }

    if (this._remainingSeconds <= 0) this._remainingSeconds = this._originalSeconds;

    this._intervalId = setInterval(() => {
      this._tick();
    }, 1000);

    this.render();
  }

  stop() {
    if (!this._intervalId) return;
    clearInterval(this._intervalId);
    this._intervalId = null;
  }

  reset(minutes = null, seconds = null) {
    if (minutes !== null || seconds !== null) {
      const m = minutes ?? Math.floor(this._originalSeconds / 60);
      const s = seconds ?? this._originalSeconds % 60;
      this.setDuration(m, s);
    } else {
      this._remainingSeconds = this._originalSeconds;
      this.render();
    }
  }

  setDuration(minutes, seconds) {
    let total = (parseInt(minutes || 0, 10) || 0) * 60 + (parseInt(seconds || 0, 10) || 0);
    if (total < 0) total = 0;
    if (total > 60 * 60) total = 60 * 60;
    this._originalSeconds = total;
    this._remainingSeconds = total;
    this.render();
  }

  _tick() {
    if (this._remainingSeconds > 0) {
      this._remainingSeconds -= 1;
      this.render();
      this.dispatchEvent(new CustomEvent('tick', { detail: { remaining: this._remainingSeconds } }));
    }

    if (this._remainingSeconds <= 0) {
      this.dispatchEvent(new CustomEvent('cycle'));
      this._remainingSeconds = this._originalSeconds;
      if (this._originalSeconds === 0) {
        this.stop();
      }
      this.render();
    }
  }

  render() {
    const mm = Math.floor(this._remainingSeconds / 60);
    const ss = this._remainingSeconds % 60;
    const text = `${String(mm).padStart(2, '0')}/${String(ss).padStart(2, '0')}`;

    // Очистка контейнера
    this._container.innerHTML = '';

    // Каждую цифру оборачиваем в span
    const splitedParts = text.split('/');
    const dots = document.createElement('span');
    dots.textContent = ':';

    const divMin = document.createElement('div');
    divMin.className = 'timer-part-wrapper timer-part-wrapper--min';
    const divSec = document.createElement('div');
    divSec.className = 'timer-part-wrapper timer-part-wrapper--sec';

    const divMinText = document.createElement('span');
    divMinText.className = 'timer-part-wrapper__text';
    divMinText.textContent = 'MIN';
    const divSecText = document.createElement('span');
    divSecText.className = 'timer-part-wrapper__text';
    divSecText.textContent = 'SEC';

    splitedParts[0].split('').forEach(char => {
      const span = document.createElement('span');
      span.textContent = char;
      span.className = 'timer-digit';
      divMin.appendChild(span);
    });

    splitedParts[1].split('').forEach(char => {
      const span = document.createElement('span');
      span.textContent = char;
      span.className = 'timer-digit';
      divSec.appendChild(span);
    });

    divMin.appendChild(divMinText);
    divSec.appendChild(divSecText);

    this._container.appendChild(divMin);
    this._container.appendChild(dots);
    this._container.appendChild(divSec);
  }

  get minutes() {
    return Math.floor(this._originalSeconds / 60);
  }

  get seconds() {
    return this._originalSeconds % 60;
  }

  getRemainingSeconds() {
    return this._remainingSeconds;
  }
}

customElements.define('timer-counter', TimerCounter);

/*
Usage examples:

<!-- start a 90-second timer (clamped to 60 minutes) and autoplay -->
<timer-counter minutes="1" seconds="30" autoplay></timer-counter>

<!-- JS control: -->
const timer = document.querySelector('timer-counter');
// start/stop/reset
timer.start();
timer.stop();
timer.reset();
// set a new duration 2 minutes 10 seconds
timer.setDuration(2, 10);

// listen to events
timer.addEventListener('tick', (e) => console.log('remaining', e.detail.remaining));
timer.addEventListener('cycle', () => console.log('cycle finished and restarted'));
*/
